/**
 * A chemical-formula parser and molar-mass calculator.
 *
 * ## What it reads
 *
 * - Elements by symbol, case-sensitive: `Co` is cobalt, `CO` is carbon monoxide.
 * - Subscripts as digits (`H2O`) or Unicode subscripts (`H₂O`).
 * - Nested groups in `()`, `[]` or `{}`: `Ca3(PO4)2`, `K4[Fe(CN)6]`, `Cu(NH3)4SO4`.
 * - Hydrates and adducts joined by `·`, `•`, `*` or `.`, each with an optional leading
 *   coefficient that may be a whole number, a decimal or a fraction:
 *   `CuSO4·5H2O`, `CuSO4*5H2O`, `CaSO4.0.5H2O`, `2CaSO4·H2O`, `CaSO4·½H2O`.
 * - A charge: `SO4^2-`, `SO4²⁻`, `SO4 2-`, `Fe^3+`, `Fe3+`, `NH4+`, `[Fe(CN)6]3-`, `Ca++`.
 * - Deuterium as `D` (`D2O`).
 *
 * ## The one ambiguity: a charge typed without a caret
 *
 * In `NH4+` the 4 is a subscript; in `Fe3+` the 3 is a charge. Plain text cannot tell, so
 * when a single sign ends the formula and no `^`, superscript or space marks the charge:
 *
 * - a digit run of two or more takes its last digit as the charge (`SO42-` is SO₄²⁻,
 *   `Hg22+` is Hg₂²⁺);
 * - a single digit after a lone element symbol is the charge (`Fe3+`, `Cu2+`, `O2-`);
 * - a single digit after a closing bracket that encloses the whole formula is the charge
 *   (`[Fe(CN)6]3-`);
 * - any other single digit is a subscript (`NH4+`, `HCO3-`, `C2H3O2-`).
 *
 * Writing `^` or a superscript always wins and is never ambiguous.
 *
 * ## Molar mass
 *
 * `molarMass` sums count × atomic weight using `elements.ts` (IUPAC/CIAAW abridged
 * weights). A charge does not change the answer: the mass of the electrons gained or lost,
 * 5.4858 × 10⁻⁴ g/mol each, is far below the precision of the atomic weights, which is
 * how general-chemistry texts treat ions.
 */

import {
  type ChemElement,
  getElement,
} from "@/lib/tool-kit/chemistry/elements";

/* ------------------------------------------------------------------ types */

/** An element with its subscript, or a bracketed group with its subscript. */
export type FormulaNode =
  | { kind: "element"; symbol: string; count: number }
  | {
      kind: "group";
      open: string;
      close: string;
      nodes: FormulaNode[];
      count: number;
    };

/** One piece of a hydrate or adduct: the leading coefficient and what follows it. */
export interface FormulaPart {
  /** The multiplier in front of the part: 5 in `·5H2O`, 0.5 in `·0.5H2O`. 1 when absent. */
  coefficient: number;
  /** The coefficient as the reader wrote it ("5", "0.5", "1/2", "½"), or "" when absent. */
  coefficientText: string;
  nodes: FormulaNode[];
}

export interface ElementCount {
  symbol: string;
  count: number;
}

export interface ParsedFormula {
  /** The formula after normalisation (subscripts and charge removed), for display. */
  parts: FormulaPart[];
  /** Net charge in elementary charges; 0 for a neutral species. */
  charge: number;
  /** Total atoms of each element over the whole formula, in order of first appearance. */
  counts: ElementCount[];
}

export type FormulaErrorCode =
  | "empty"
  | "unexpected"
  | "unknownElement"
  | "unbalanced"
  | "zeroCount"
  | "emptyGroup"
  | "badCharge"
  | "tooLarge";

export interface FormulaError {
  code: FormulaErrorCode;
  /** The offending text: an unknown symbol, a stray character, an unmatched bracket. */
  token: string;
}

export type ParseResult =
  | { ok: true; formula: ParsedFormula }
  | { ok: false; error: FormulaError };

/* -------------------------------------------------------- normalisation */

const SUBSCRIPTS = "₀₁₂₃₄₅₆₇₈₉";
const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const SUPERSCRIPT_RUN = /[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻]+/g;
const SUBSCRIPT_RUN = /[₀-₉]/g;
const MINUS_LIKE = /[−–—‒﹣－]/g;
const PLUS_LIKE = /[＋﹢]/g;
const DOT_SEPARATORS = /[•∙⋅·*]/g;
const LATEX_SUB = /_\{?(\d+)\}?/g;
const LATEX_SUP = /\^\{([^}]*)\}/g;
const WHITESPACE = /\s+/g;
const AROUND_DOT = /\s*·\s*/g;
const MAX_LENGTH = 200;
const MAX_COUNT = 9999;
const MAX_DEPTH = 12;

function fromSuperscript(run: string): string {
  let out = "";
  for (const char of run) {
    const digit = SUPERSCRIPT_DIGITS.indexOf(char);
    if (digit >= 0) {
      out += String(digit);
    } else {
      out += char === "⁺" ? "+" : "-";
    }
  }
  return out;
}

/** Straighten Unicode subscripts, superscripts, minus signs and dots into ASCII. */
function normalise(raw: string): string {
  return raw
    .replace(SUBSCRIPT_RUN, (char) => String(SUBSCRIPTS.indexOf(char)))
    .replace(SUPERSCRIPT_RUN, (run) => `^${fromSuperscript(run)}`)
    .replace(MINUS_LIKE, "-")
    .replace(PLUS_LIKE, "+")
    .replace(LATEX_SUB, "$1")
    .replace(LATEX_SUP, "^$1")
    .replace(DOT_SEPARATORS, "·")
    .replace(AROUND_DOT, "·")
    .replace(WHITESPACE, " ")
    .trim();
}

/* --------------------------------------------------------------- charge */

const CARET_CHARGE = /\^\s*(?:(\d+)\s*([+-])|([+-])\s*(\d+)|([+-]+))\s*$/;
const SPACED_CHARGE = / (?:(\d+)\s*([+-])|([+-]+))$/;
const PAREN_CHARGE = /\(\s*(?:(\d+)\s*([+-])|([+-])\s*(\d+)|([+-]+))\s*\)$/;
const BARE_SIGNS = /([+-]+)$/;
const TRAILING_DIGITS = /(\d+)$/;
const LONE_ELEMENT = /^[A-Z][a-z]?$/;
const CLOSERS = new Set([")", "]", "}"]);

interface ChargeSplit {
  body: string;
  charge: number;
}

function signed(magnitude: number, sign: string): number {
  return sign.startsWith("-") ? -magnitude : magnitude;
}

/** A charge written with a marker — `^2-`, ` 2-`, `(2+)` — and so not ambiguous. */
function explicitCharge(text: string): ChargeSplit | null {
  for (const pattern of [CARET_CHARGE, SPACED_CHARGE, PAREN_CHARGE]) {
    const match = pattern.exec(text);
    if (!match) {
      continue;
    }
    const body = text.slice(0, match.index).trim();
    const [, digitsA, signA, signB, digitsB, signs] = match;
    if (digitsA && signA) {
      return { body, charge: signed(Number(digitsA), signA) };
    }
    if (signB && digitsB) {
      return { body, charge: signed(Number(digitsB), signB) };
    }
    if (signs) {
      return { body, charge: signed(signs.length, signs) };
    }
  }
  return null;
}

/** True when the bracket that closes `body` opens at its first character. */
function closesWholeFormula(body: string): boolean {
  if (!CLOSERS.has(body.at(-1) ?? "")) {
    return false;
  }
  let depth = 0;
  for (let i = 0; i < body.length; i++) {
    const char = body[i];
    if (char === "(" || char === "[" || char === "{") {
      depth++;
    } else if (CLOSERS.has(char)) {
      depth--;
      if (depth === 0) {
        return i === body.length - 1;
      }
    }
  }
  return false;
}

/** The charge of a formula ending in a bare `+` or `-`, with the class's digit heuristics. */
function bareCharge(text: string): ChargeSplit | null {
  const signs = BARE_SIGNS.exec(text);
  if (!signs) {
    return null;
  }
  const run = signs[1];
  let body = text.slice(0, signs.index);
  if (run.length > 1) {
    return { body, charge: signed(run.length, run) };
  }
  const digits = TRAILING_DIGITS.exec(body)?.[1] ?? "";
  let magnitude = 1;
  if (digits.length >= 2) {
    magnitude = Number(digits.at(-1));
    body = body.slice(0, -1);
  } else if (digits.length === 1) {
    const before = body.slice(0, -1);
    if (LONE_ELEMENT.test(before) || closesWholeFormula(before)) {
      magnitude = Number(digits);
      body = before;
    }
  }
  return { body, charge: signed(magnitude, run) };
}

function splitCharge(text: string): ChargeSplit {
  return explicitCharge(text) ?? bareCharge(text) ?? { body: text, charge: 0 };
}

/* --------------------------------------------------------------- parser */

class FormulaParseError extends Error {
  readonly error: FormulaError;
  constructor(code: FormulaErrorCode, token: string) {
    super(code);
    this.error = { code, token };
  }
}

const COEFFICIENT = /^(?:(\d+)\/(\d+)|(\d+(?:\.\d+)?)|([½¼¾⅓⅔]))/;
const UNICODE_FRACTIONS: Record<string, number> = {
  "½": 0.5,
  "¼": 0.25,
  "¾": 0.75,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
};
const OPENERS: Record<string, string> = { "(": ")", "[": "]", "{": "}" };
const CLOSE_BRACKETS = new Set([")", "]", "}"]);
const UPPER = /[A-Z]/;
const LOWER = /[a-z]/;
const DIGIT = /[0-9]/;

class Reader {
  private pos = 0;
  private readonly text: string;

  constructor(text: string) {
    this.text = text;
  }

  get done(): boolean {
    return this.pos >= this.text.length;
  }

  peek(): string {
    return this.text[this.pos] ?? "";
  }

  next(): string {
    const char = this.text[this.pos] ?? "";
    this.pos++;
    return char;
  }

  rest(): string {
    return this.text.slice(this.pos);
  }

  advance(length: number): void {
    this.pos += length;
  }

  /** Read an integer subscript; 1 when none is written. */
  count(): number {
    let digits = "";
    while (DIGIT.test(this.peek())) {
      digits += this.next();
    }
    if (digits === "") {
      return 1;
    }
    const value = Number(digits);
    if (value === 0) {
      throw new FormulaParseError("zeroCount", digits);
    }
    if (value > MAX_COUNT) {
      throw new FormulaParseError("tooLarge", digits);
    }
    return value;
  }

  symbol(): string {
    let token = this.next();
    while (LOWER.test(this.peek())) {
      token += this.next();
    }
    return token;
  }
}

function parseElement(reader: Reader): FormulaNode {
  const symbol = reader.symbol();
  if (!getElement(symbol)) {
    throw new FormulaParseError("unknownElement", symbol);
  }
  return { kind: "element", symbol, count: reader.count() };
}

function parseGroup(reader: Reader, depth: number): FormulaNode {
  const open = reader.next();
  const close = OPENERS[open];
  const nodes = parseNodes(reader, close, depth + 1);
  if (reader.peek() !== close) {
    throw new FormulaParseError("unbalanced", open);
  }
  reader.next();
  if (nodes.length === 0) {
    throw new FormulaParseError("emptyGroup", open + close);
  }
  return { kind: "group", open, close, nodes, count: reader.count() };
}

function endsSequence(char: string, closer: string): boolean {
  return char === closer || char === "·" || (closer === "" && char === ".");
}

function parseNodes(
  reader: Reader,
  closer: string,
  depth: number
): FormulaNode[] {
  if (depth > MAX_DEPTH) {
    throw new FormulaParseError("tooLarge", "(");
  }
  const nodes: FormulaNode[] = [];
  while (!(reader.done || endsSequence(reader.peek(), closer))) {
    const char = reader.peek();
    if (UPPER.test(char)) {
      nodes.push(parseElement(reader));
    } else if (char in OPENERS) {
      nodes.push(parseGroup(reader, depth));
    } else if (CLOSE_BRACKETS.has(char)) {
      throw new FormulaParseError("unbalanced", char);
    } else {
      throw new FormulaParseError("unexpected", char);
    }
  }
  return nodes;
}

function readCoefficient(reader: Reader): { value: number; text: string } {
  const match = COEFFICIENT.exec(reader.rest());
  if (!match) {
    return { value: 1, text: "" };
  }
  const text = match[0];
  let value: number;
  if (match[1] !== undefined) {
    value = Number(match[1]) / Number(match[2]);
  } else if (match[3] !== undefined) {
    value = Number(match[3]);
  } else {
    value = UNICODE_FRACTIONS[match[4]];
  }
  if (!Number.isFinite(value) || value <= 0) {
    throw new FormulaParseError("zeroCount", text);
  }
  reader.advance(text.length);
  return { value, text };
}

function parseParts(body: string): FormulaPart[] {
  const reader = new Reader(body);
  const parts: FormulaPart[] = [];
  do {
    if (reader.peek() === "·" || reader.peek() === ".") {
      reader.next();
    }
    const { value, text } = readCoefficient(reader);
    const nodes = parseNodes(reader, "", 0);
    if (nodes.length === 0) {
      throw new FormulaParseError(
        reader.done ? "empty" : "unexpected",
        reader.peek()
      );
    }
    parts.push({ coefficient: value, coefficientText: text, nodes });
  } while (reader.peek() === "·" || reader.peek() === ".");
  if (!reader.done) {
    throw new FormulaParseError("unexpected", reader.peek());
  }
  return parts;
}

/* ------------------------------------------------------------- counting */

/** Strip float noise (0.30000000000000004 → 0.3) without losing a real digit. */
function tidy(value: number): number {
  return Number(value.toPrecision(12));
}

function addCounts(
  nodes: readonly FormulaNode[],
  multiplier: number,
  into: Map<string, number>
): void {
  for (const node of nodes) {
    if (node.kind === "element") {
      into.set(
        node.symbol,
        (into.get(node.symbol) ?? 0) + node.count * multiplier
      );
    } else {
      addCounts(node.nodes, multiplier * node.count, into);
    }
  }
}

/** Atoms of each element in `nodes`, in order of first appearance. */
export function countAtoms(
  nodes: readonly FormulaNode[],
  multiplier = 1
): ElementCount[] {
  const map = new Map<string, number>();
  addCounts(nodes, multiplier, map);
  return [...map].map(([symbol, count]) => ({ symbol, count: tidy(count) }));
}

/* ----------------------------------------------------------- public API */

/** Parse a formula. Never throws: a bad formula comes back as `{ ok: false, error }`. */
export function parseFormula(raw: string): ParseResult {
  if (raw.length > MAX_LENGTH) {
    return { ok: false, error: { code: "tooLarge", token: "" } };
  }
  const text = normalise(raw);
  if (text === "") {
    return { ok: false, error: { code: "empty", token: "" } };
  }
  try {
    const { body, charge } = splitCharge(text);
    if (body === "") {
      throw new FormulaParseError("badCharge", text);
    }
    const parts = parseParts(body.replace(WHITESPACE, ""));
    const map = new Map<string, number>();
    for (const part of parts) {
      addCounts(part.nodes, part.coefficient, map);
    }
    const counts = [...map].map(([symbol, count]) => ({
      symbol,
      count: tidy(count),
    }));
    return { ok: true, formula: { parts, charge, counts } };
  } catch (error) {
    if (error instanceof FormulaParseError) {
      return { ok: false, error: error.error };
    }
    throw error;
  }
}

/* ----------------------------------------------------------- molar mass */

export interface MassRow {
  symbol: string;
  name: string;
  count: number;
  /** Atomic weight, g/mol. */
  weight: number;
  /** count × weight, g/mol. */
  mass: number;
  /** Mass fraction of the formula, 0–1. */
  fraction: number;
  /** The weight is an isotope's mass number, not a standard atomic weight. */
  massNumber: boolean;
}

export interface MolarMass {
  /** g/mol */
  total: number;
  rows: MassRow[];
  /** Total atoms per formula unit. */
  atoms: number;
  /** At least one element has only a mass number (no IUPAC standard atomic weight). */
  usesMassNumber: boolean;
}

/** Molar mass from element counts. Unknown symbols are skipped (the parser never emits one). */
export function molarMassOf(counts: readonly ElementCount[]): MolarMass {
  const rows: MassRow[] = [];
  let total = 0;
  let atoms = 0;
  for (const { symbol, count } of counts) {
    const element: ChemElement | undefined = getElement(symbol);
    if (!element) {
      continue;
    }
    const mass = tidy(count * element.weight);
    total += mass;
    atoms += count;
    rows.push({
      symbol,
      name: element.name,
      count,
      weight: element.weight,
      mass,
      fraction: 0,
      massNumber: element.massNumber,
    });
  }
  total = tidy(total);
  for (const row of rows) {
    row.fraction = total === 0 ? 0 : row.mass / total;
  }
  return {
    total,
    rows,
    atoms: tidy(atoms),
    usesMassNumber: rows.some((row) => row.massNumber),
  };
}

export function molarMass(formula: ParsedFormula): MolarMass {
  return molarMassOf(formula.counts);
}

/** Molar mass of one part (per formula unit of that part, coefficient not applied). */
export function partMolarMass(part: FormulaPart): number {
  return molarMassOf(countAtoms(part.nodes)).total;
}

/* --------------------------------------------------------------- display */

export interface FormulaSegment {
  text: string;
  style: "normal" | "sub" | "sup";
}

function pushNodes(nodes: readonly FormulaNode[], out: FormulaSegment[]): void {
  for (const node of nodes) {
    if (node.kind === "element") {
      out.push({ text: node.symbol, style: "normal" });
    } else {
      out.push({ text: node.open, style: "normal" });
      pushNodes(node.nodes, out);
      out.push({ text: node.close, style: "normal" });
    }
    if (node.count > 1) {
      out.push({ text: String(node.count), style: "sub" });
    }
  }
}

/** The sign and magnitude of a charge as text: 2−, +, 3+. */
export function chargeText(charge: number): string {
  if (charge === 0) {
    return "";
  }
  const sign = charge > 0 ? "+" : "−";
  const size = Math.abs(charge);
  return size === 1 ? sign : `${size}${sign}`;
}

/** Subscripts and superscripts as styled runs, for `<sub>` / `<sup>` rendering. */
export function formulaSegments(formula: ParsedFormula): FormulaSegment[] {
  const out: FormulaSegment[] = [];
  for (const [index, part] of formula.parts.entries()) {
    if (index > 0) {
      out.push({ text: "·", style: "normal" });
    }
    if (part.coefficientText !== "") {
      out.push({ text: part.coefficientText, style: "normal" });
    }
    pushNodes(part.nodes, out);
  }
  if (formula.charge !== 0) {
    out.push({ text: chargeText(formula.charge), style: "sup" });
  }
  return out;
}

const SUB_DIGITS = "₀₁₂₃₄₅₆₇₈₉";
const SUP_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function mapDigits(
  text: string,
  table: string,
  plus: string,
  minus: string
): string {
  let out = "";
  for (const char of text) {
    if (char === "+") {
      out += plus;
    } else if (char === "−") {
      out += minus;
    } else {
      out += table[Number(char)] ?? char;
    }
  }
  return out;
}

/** The formula as plain Unicode text — `Ca(OH)₂`, `CuSO₄·5H₂O`, `SO₄²⁻` — for copying. */
export function formulaToUnicode(formula: ParsedFormula): string {
  return formulaSegments(formula)
    .map((segment) => {
      if (segment.style === "sub") {
        return mapDigits(segment.text, SUB_DIGITS, "₊", "₋");
      }
      if (segment.style === "sup") {
        return mapDigits(segment.text, SUP_DIGITS, "⁺", "⁻");
      }
      return segment.text;
    })
    .join("");
}

/** One part of a hydrate as plain Unicode text, without its coefficient: `H₂O`. */
export function partToUnicode(part: FormulaPart): string {
  return formulaToUnicode({
    parts: [{ ...part, coefficient: 1, coefficientText: "" }],
    charge: 0,
    counts: [],
  });
}
